import React, { FC } from 'react'
import {
	CardPriorities,
	BoardStatusCard,
} from '../../../../lib/api/types/board.interfaces'
import cn from '../../../../lib/utils/cn'
import {
	Delete,
	Done,
	DragIndicator,
	Edit,
	NoAccountsOutlined,
	QueryBuilder,
} from '@mui/icons-material'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import isToday from 'dayjs/plugin/isToday'
import isTomorrow from 'dayjs/plugin/isTomorrow'
import { IconButton, OptionsMenuButton } from '../../../../components/ui'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useAuth } from '../../../../hooks'

dayjs.extend(relativeTime)
dayjs.extend(isToday)
dayjs.extend(isTomorrow)

interface ColumnCardProps {
	data: BoardStatusCard
	onOpen: () => void
	onEdit: () => void
	onMove: () => void
	onDelete: () => void
	index: number
	columnId: string
	isMobile?: boolean
	isMobileList?: boolean
	isComplete?: boolean
	isOwner?: boolean
}

const ColumnCard: FC<ColumnCardProps> = ({
	data,
	onOpen,
	onEdit,
	onMove,
	onDelete,
	index,
	columnId,
	isMobile,
	isMobileList,
	isComplete,
	isOwner,
}) => {
	const { currentUser } = useAuth()

	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({
		id: data.id,
		data: {
			type: 'card',
			index,
			columnId,
			card: data,
		},
	})

	const style = {
		transform: CSS.Transform.toString(transform),
		transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
		opacity: isDragging ? 0.5 : 1,
		display: isDragging ? 'none' : undefined,
		willChange: 'transform',
	}

	const isAssignment = data?.assignedTo?.id === currentUser?.id
	return (
		<div
			ref={setNodeRef}
			style={style}
			onClick={onOpen}
			className="w-full border group/card relative border-stroke-dark dark:border-stroke-light-dark rounded-lg p-3 box-border bg-white dark:bg-background-light-dark hover:bg-background-light-hover duration-200 cursor-pointer h-[120px] flex flex-col justify-between transition-all will-change-transform"
		>
			<div className="h-10 flex justify-between">
				<p
					className={cn(
						'dark:text-text-table-dark md:w-[220px] h-10 text-sm font-medium',
						'whitespace-normal line-clamp-2 overflow-ellipsis',
						isOwner
							? 'md:group-hover/card:w-[155px]'
							: 'md:group-hover/card:w-[210px]',
						isMobileList && (isOwner ? 'max-w-[90%]' : 'max-w-[95%]')
					)}
				>
					{data?.title}
				</p>
				{isMobile ? (
					<div className="flex items-center">
						<div className="relative">
							{(isOwner || isAssignment) && (
								<OptionsMenuButton
									defaultPosition={{
										top: 20,
										right: 0,
									}}
									items={[
										{
											label: 'Move',
											icon: (
												<Edit className="w-4 h-4 fill-text-secondary dark:fill-text-table-dark" />
											),
											onClick: onMove,
										},
										{
											label: 'Edit',
											icon: (
												<Edit className="w-4 h-4 fill-text-secondary dark:fill-text-table-dark" />
											),
											onClick: onEdit,
											visible: isOwner,
										},
										{
											label: 'Delete',
											icon: (
												<Delete className="w-4 h-4 fill-text-secondary dark:fill-text-table-dark" />
											),
											onClick: onDelete,
											visible: isOwner && data?.initialStatusId === columnId,
										},
									]}
								/>
							)}
						</div>
					</div>
				) : (
					<div className="flex items-center justify-end gap-1 ml-auto duration-default absolute top-2 right-2 md:opacity-0 md:group-hover/card:opacity-100">
						{isOwner && data?.initialStatusId === columnId && (
							<IconButton
								className="w-5 h-5 px-3"
								variant="transparent"
								icon={
									<Delete className="w-4 h-4 fill-text-secondary dark:fill-text-table-dark" />
								}
								onClick={onDelete}
								tooltip="Delete"
							/>
						)}
						{isOwner && (
							<IconButton
								className="w-5 h-5 px-3"
								variant="transparent"
								icon={
									<Edit className="w-4 h-4 fill-text-secondary dark:fill-text-table-dark" />
								}
								onClick={onEdit}
								tooltip="Edit"
							/>
						)}

						{(isOwner || isAssignment) && (
							<IconButton
								className="w-5 h-5 px-3 hover:cursor-grab"
								variant="transparent"
								icon={
									<DragIndicator className="w-4 h-4 fill-text-secondary dark:fill-text-table-dark" />
								}
								{...attributes}
								{...listeners}
								tooltip="Move"
							/>
						)}
					</div>
				)}
			</div>
			<div className="flex items-center justify-between w-full py-2">
				<div className="flex flex-col gap-1 dark:text-text-table-dark">
					{!isComplete && <PriorityLabel priority={data?.priority || null} />}
					<TimeLabel
						dueDate={data?.dueDate || null}
						completedAt={isComplete ? data?.completedAt || null : null}
					/>
				</div>

				<IconButton
					variant="transparent"
					className="w-8 h-8 p-0 bg-background-light dark:bg-background-dark"
					icon={
						data?.assignedTo?.avatar ? (
							<img
								className="w-full h-full rounded-lg object-cover"
								src={data?.assignedTo?.avatar}
								alt={data?.assignedTo?.name || 'User avatar'}
							/>
						) : (
							<span className="w-full h-full flex items-center justify-center rounded-lg text-xs">
								{data?.assignedTo ? (
									data?.assignedTo?.name?.[0] || '?'
								) : (
									<NoAccountsOutlined className="fill-text-secondary dark:fill-stroke-dark" />
								)}
							</span>
						)
					}
					tooltip={
						data?.assignedTo?.name || data?.assignedTo?.email || 'Unassigned'
					}
					classNameWrapper="min-w-[60px] max-w-[130px] px-2 text-center"
					tooltipPosition="top"
				/>
			</div>
		</div>
	)
}
export default ColumnCard

export const PriorityLabel = ({
	priority,
	className,
	isShrink,
}: {
	priority?: number | null
	className?: string
	isShrink?: boolean
}) => {
	if (!priority || priority > 3) return null
	return (
		<p
			className={cn(
				'px-1.5 h-4 flex items-center justify-center text-white !uppercase font-medium text-xs w-fit rounded dark:text-text-main',
				priority === 1 && 'bg-positive',
				priority === 2 && 'bg-accent',
				priority === 3 && 'bg-negative',
				className
			)}
		>
			{CardPriorities[priority as 1 | 2 | 3] + (isShrink ? '' : ' Priority')}
		</p>
	)
}

export const TimeLabel = ({
	dueDate,
	completedAt,
}: Record<string, string | Date | null>) => {
	if (completedAt) {
		return (
			<div className="flex items-center gap-2">
				<Done className="fill-positive w-3 h-3" />
				<span className="text-text-secondary text-xs dark:text-stroke-dark">
					{dayjs(completedAt)?.format('MMM D, YYYY [at] h:mm A')}
				</span>
			</div>
		)
	}

	if (!dueDate) return null

	const date = dayjs(dueDate)
	const now = dayjs()
	const diffDays = date.diff(now, 'day')
	const absDiffDays = Math.abs(diffDays)
	const getTimeInfo = () => {
		switch (true) {
			case date.isToday():
				return {
					text: 'Today',
					colorClass: 'text-yellow-400',
					iconFill: 'fill-yellow-400',
				}

			case diffDays === 0:
				return {
					text: `1d left`,
					colorClass: 'text-text-secondary dark:text-stroke-dark',
					iconFill: 'fill-text-secondary dark:fill-text-table-dark',
				}

			case diffDays > 0 && absDiffDays < 31:
				return {
					text: `${absDiffDays}d left`,
					colorClass: 'text-text-secondary dark:text-stroke-dark',
					iconFill: 'fill-text-secondary dark:fill-text-table-dark',
				}

			case diffDays > 30:
				return {
					text: date.format('MMM D, YYYY'),
					colorClass: 'text-text-secondary dark:text-stroke-dark',
					iconFill: 'fill-text-secondary dark:fill-text-table-dark',
				}

			case diffDays < 0:
				return {
					text: 'Expired on ' + date.format('MMM D, YYYY'),
					colorClass: 'text-red-400',
					iconFill: 'fill-red-400',
				}

			default:
				return {
					text: date.format('MMM D, YYYY'),
					colorClass: 'text-text-secondary dark:text-stroke-dark',
					iconFill: 'fill-text-secondary dark:fill-text-table-dark',
				}
		}
	}

	const { text, colorClass, iconFill } = getTimeInfo()

	return (
		<span className={cn('text-[10px] flex items-center gap-1', colorClass)}>
			<QueryBuilder className={cn('w-3.5 h-3.5', iconFill)} />
			{text}
		</span>
	)
}
